import {
	getAnimalQueryOptions,
	listHerdsByOrganizationQueryOptions,
} from "@packages/api-client/hooks/livestockController";
import type { LivestockHerdWithStats } from "@packages/shared-types/livestock";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useCreateMeasurement } from "@/api/livestock";
import { PageHeader } from "@/components/PageHeader";
import { useAnimalOptions } from "@/hooks/useAnimalOptions";
import { MeasurementForm } from "../../../measurements/-components/MeasurementForm";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/livestock/$establishmentId/animals/$animalId/measurements/new",
)({
	staticData: { offline: "ready" },
	loader: ({ context, params }) => {
		// Fire-and-forget prefetch (animals/new precedent): if cached (online or
		// from IDB), the component sees data immediately; if not cached offline,
		// the form still renders — animal options fall back to the prepared
		// dataset via useAnimalOptions, herd options degrade to empty.
		context.queryClient.ensureQueryData({
			...getAnimalQueryOptions(params.orgId, params.animalId),
			staleTime: Number.POSITIVE_INFINITY,
		});
		context.queryClient.ensureQueryData({
			...listHerdsByOrganizationQueryOptions(params.orgId, {
				establishmentId: params.establishmentId,
			}),
			staleTime: Number.POSITIVE_INFINITY,
		});
	},
	component: NewAnimalMeasurementPage,
});

function NewAnimalMeasurementPage() {
	const { orgId, establishmentId, animalId } = Route.useParams();
	const { t } = useTranslation();
	const navigate = Route.useNavigate();

	const { data: herdsData } = useQuery(
		listHerdsByOrganizationQueryOptions(orgId, { establishmentId }),
	);

	const { options: animals } = useAnimalOptions({ orgId, establishmentId });

	const createMeasurement = useCreateMeasurement();

	const herds = (herdsData?.data ?? []) as LivestockHerdWithStats[];

	const goBack = () => {
		navigate({
			to: "/app/org/$orgId/livestock/$establishmentId/animals/$animalId",
			params: { orgId, establishmentId, animalId },
		});
	};

	return (
		<div className="flex flex-col gap-6">
			<PageHeader
				title={t("livestock.measurements.create.title")}
				showBackButton
			/>

			<MeasurementForm
				orgId={orgId}
				establishmentId={establishmentId}
				animals={animals}
				herds={herds}
				lockedAnimalId={animalId}
				onSubmit={(data) =>
					createMeasurement.mutateAsync(
						{ organizationId: orgId, data },
						{ onSuccess: goBack },
					)
				}
				onSaveAndAddAnother={(data) =>
					// Batch entry: record and stay on the form (it resets itself,
					// keeping the measurement type/date for the next reading).
					createMeasurement.mutateAsync({ organizationId: orgId, data })
				}
				isSubmitting={createMeasurement.isPending}
				onCancel={goBack}
			/>
		</div>
	);
}
